import React from 'react';

/**
 * HembesökSection — fristående sektion med 3 knappar för hembesöks-hantering.
 * Funkar oberoende av om estimatet är godkänt eller ej, och visas alltid när
 * estimatet är skickat.
 *
 * Knappar:
 *  - Boka hembesök direkt → öppnar BookHomeVisitModal
 *  - Föreslå tider för hembesök → öppnar ProposeHomeVisitTimesModal
 *  - Hoppa över hembesök → uppdaterar procurement direkt (homeVisitSkipped=true)
 *
 * Statusbanner visas om hembesök redan är bokat / hoppats över / förslag skickade.
 */
const HembesökSection = ({
  currentSentAt,
  expanded,
  onToggle,
  onBookDirectly,
  onProposeTimes,
  onSkipHomeVisit,
  onEditBooked,
  onCancelProposals,
  onCancelSingleProposal,
  isCancellingProposals = false,
  skipLoading = false,
  homeVisitStatus = null, // { booked, skipped, proposalsSent, sentProposals, customerProposalsSent, customerProposals, customerMessage, customerAccepted, acceptedProposal }
}) => {
  if (!currentSentAt) return null;

  const isBooked = !!homeVisitStatus?.booked;
  const isSkipped = !!homeVisitStatus?.skipped;
  const isCustomerAccepted = !!homeVisitStatus?.customerAccepted;
  // Dölj default-knapparna när kund accepterat eller föreslagit egna tider —
  // i de fallen är nästa steg "boka tiden" eller "boka kundens förslag", inte "börja om".
  const showActionButtons = !isBooked && !isSkipped && !isCustomerAccepted && !homeVisitStatus?.customerProposalsSent;

  const renderStatus = () => {
    if (!homeVisitStatus) return null;
    if (homeVisitStatus.skipped) {
      return (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
          ✓ Hembesök hoppades över — gå vidare till kalkyl.
        </div>
      );
    }
    if (homeVisitStatus.booked) {
      const slotText = homeVisitStatus.bookedSlotText || 'Hembesök bokat';
      return (
        <ul className="space-y-2 text-sm text-gray-700 mb-2">
          <li className="flex w-fit max-w-full items-center gap-3 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            <span className="text-gray-900">{slotText}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-2.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-1 bg-green-700 text-white opacity-80 cursor-not-allowed"
                title="Hembesöket är bokat"
                disabled
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Bokad
              </button>
              {onEditBooked && (
                <button
                  type="button"
                  onClick={onEditBooked}
                  className="px-2.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-1 text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  title="Ändra hembesökstid"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Ändra
                </button>
              )}
            </div>
          </li>
        </ul>
      );
    }
    if (homeVisitStatus.customerProposalsSent && Array.isArray(homeVisitStatus.customerProposals) && homeVisitStatus.customerProposals.length > 0) {
      return (
        <div className="text-sm bg-orange-50 border border-orange-200 rounded-lg px-3 py-3 mb-3">
          <div className="font-medium text-orange-900 mb-2">🏠 Kunden har föreslagit följande tider:</div>
          <div className="space-y-1.5">
            {homeVisitStatus.customerProposals.map((proposal, idx) => {
              const display = proposal?.displayText || `${proposal?.date || ''} kl ${proposal?.time || ''}`;
              return (
                <div key={proposal?._id || idx} className="flex w-fit max-w-full items-center gap-3 bg-white border border-orange-200 rounded px-3 py-2">
                  <span className="text-orange-900">{display}</span>
                  <button
                    type="button"
                    onClick={() => onBookDirectly(proposal?.date || '', proposal?.time || '')}
                    className="px-3 py-1 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                  >
                    Boka denna tid
                  </button>
                </div>
              );
            })}
          </div>
          {homeVisitStatus.customerMessage && (
            <div className="mt-2 pt-2 border-t border-orange-200">
              <span className="text-xs text-orange-700">💬 Meddelande från kund:</span>
              <div className="text-orange-900 italic mt-1">"{homeVisitStatus.customerMessage}"</div>
            </div>
          )}

          {/* Passar ingen av kundens tider måste man kunna föreslå egna — utan
              det här var enda vägen framåt att boka en av kundens tider. */}
          <div className="mt-3 pt-3 border-t border-orange-200 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onProposeTimes}
              className="text-xs px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Föreslå andra tider
            </button>
            <button
              type="button"
              onClick={() => onBookDirectly()}
              className="text-xs px-3 py-2 rounded bg-leadme-gray-dark text-white hover:bg-leadme-gray transition-colors"
            >
              Boka annan tid
            </button>
            <button
              type="button"
              onClick={onSkipHomeVisit}
              disabled={skipLoading}
              className="text-xs px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
              title="Hoppa över hembesök och gå direkt till kalkyl"
            >
              {skipLoading ? 'Hoppar över…' : 'Hoppa över'}
            </button>
          </div>
        </div>
      );
    }
    if (homeVisitStatus.customerAccepted && homeVisitStatus.acceptedProposal) {
      const proposal = homeVisitStatus.acceptedProposal;
      const display = proposal.displayText || `${proposal.date || ''} kl ${proposal.time || ''}`;
      return (
        <div className="text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-3 mb-3">
          <div className="font-medium text-green-900 mb-2">✓ Kunden har accepterat din tid:</div>
          <div className="flex w-fit max-w-full items-center gap-3 bg-white border border-green-200 rounded px-3 py-2">
            <span className="text-green-900">{display}</span>
            <button
              type="button"
              onClick={() => onBookDirectly(proposal.date || '', proposal.time || '')}
              className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
            >
              Boka mötet
            </button>
          </div>
        </div>
      );
    }
    if (homeVisitStatus.proposalsSent) {
      const sent = Array.isArray(homeVisitStatus.sentProposals) ? homeVisitStatus.sentProposals : [];
      return (
        /* Samma uppställning som uppföljningens KonsultProposalsWaiting */
        <div>
          <div className="text-sm text-gray-700 mb-4">
            <strong>Skickade förslag till kund</strong>
          </div>

          {sent.length > 0 ? (
            <ul className="space-y-2 text-sm text-gray-700 mb-4">
              {sent.map((p, i) => (
                /* w-fit: raden blir bara så bred som innehållet, så krysset
                   hamnar intill tiden i stället för ute vid högerkanten */
                <li
                  key={p?._id || i}
                  className="flex w-fit max-w-full items-center gap-3 bg-yellow-50 border border-yellow-200 rounded px-3 py-2"
                >
                  <span className="text-gray-900">{p?.displayText || `${p?.date || ''} kl ${p?.time || ''}`}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                      Väntar på kundsvar
                    </span>
                    {/*
                      Återkalla just den här tiden. Är det sista förslaget dras hela
                      utskicket tillbaka — bekräftelsedialogen säger det.
                    */}
                    {onCancelSingleProposal && p?._id && (
                      <button
                        type="button"
                        onClick={() => onCancelSingleProposal(p, sent.length)}
                        disabled={isCancellingProposals}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title={`Återkalla ${p?.displayText || 'tidsförslaget'}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500 italic mb-4">Inga förslag att visa.</div>
          )}

          {onCancelProposals && (
            <button
              type="button"
              onClick={onCancelProposals}
              disabled={isCancellingProposals}
              className="mb-3 px-2.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap inline-flex items-center gap-1 text-red-600 bg-white border border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={sent.length > 1
                ? 'Återkalla samtliga skickade tidsförslag och avboka de preliminära tiderna. Enskilda förslag återkallas med krysset intill tiden.'
                : 'Återkalla tidsförslaget och avboka den preliminära tiden'}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a4 4 0 010 8h-1m-9-8l4-4m-4 4l4 4" />
              </svg>
              {isCancellingProposals
                ? 'Återkallar...'
                : sent.length > 1 ? 'Återkalla alla tidsförslag' : 'Återkalla tidsförslaget'}
            </button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
      <div
        className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-leadme-gray-dark">Hembesök</h2>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="p-4 sm:p-6">
          {renderStatus()}

          {showActionButtons && (
            <>
              {/* Samma kompakta knappstil som uppföljningens FollowupActions */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={onProposeTimes}
                  className="text-xs px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Föreslå tid
                </button>
                <button
                  type="button"
                  // Utan wrappern skickas klick-eventet in som datum
                  onClick={() => onBookDirectly()}
                  className="text-xs px-3 py-2 rounded bg-leadme-gray-dark text-white hover:bg-leadme-gray transition-colors"
                >
                  Boka hembesök
                </button>
                <button
                  type="button"
                  onClick={onSkipHomeVisit}
                  disabled={skipLoading}
                  className="text-xs px-3 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                  title="Hoppa över hembesök och gå direkt till kalkyl"
                >
                  {skipLoading ? (
                    <>
                      <svg className="animate-spin h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Hoppar över…
                    </>
                  ) : (
                    'Hoppa över'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HembesökSection;
